class PPOrderTracking extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.form = this.querySelector('[data-tracking-form]');
    this.input = this.querySelector('[data-tracking-input]');
    this.submitButton = this.querySelector('[data-tracking-submit]');
    this.status = this.querySelector('[data-tracking-status]');
    this.result = this.querySelector('[data-tracking-result]');
    this.currentStatus = this.querySelector('[data-tracking-current-status]');
    this.trackingNumber = this.querySelector('[data-tracking-number]');
    this.updated = this.querySelector('[data-tracking-updated]');
    this.estimateRow = this.querySelector('[data-tracking-estimate-row]');
    this.estimate = this.querySelector('[data-tracking-estimate]');
    this.timelineWrap = this.querySelector('[data-tracking-timeline-wrap]');
    this.timeline = this.querySelector('[data-tracking-timeline]');

    this.form?.addEventListener('submit', this.handleSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
  }

  statusLabel(status) {
    const normalizedStatus = String(status || 'unknown')
      .trim()
      .toLowerCase()
      .replaceAll('_', '-');
    const statusSuffix = normalizedStatus.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
    const datasetKey = `status${statusSuffix.charAt(0).toUpperCase()}${statusSuffix.slice(1)}`;

    return this.dataset[datasetKey] || this.dataset.statusUnknown;
  }

  formatDate(value, includeTime = false) {
    if (!value) return '';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
      dateStyle: 'medium',
      ...(includeTime ? { timeStyle: 'short' } : {}),
    }).format(date);
  }

  renderResult(shipment, submittedTrackingNumber) {
    const events = Array.isArray(shipment.events) ? shipment.events : [];
    const lastUpdated = this.formatDate(shipment.last_updated_at, true);
    const estimatedDelivery = this.formatDate(shipment.estimated_delivery_at);

    this.currentStatus.textContent = this.statusLabel(shipment.status);
    this.trackingNumber.textContent = shipment.tracking_number || submittedTrackingNumber;
    this.updated.textContent = lastUpdated ? `${this.dataset.updatedLabel} ${lastUpdated}` : '';

    this.estimate.textContent = estimatedDelivery;
    this.estimateRow.hidden = !estimatedDelivery;

    this.timeline.replaceChildren();
    events.forEach((event) => {
      const item = document.createElement('li');
      const marker = document.createElement('span');
      const content = document.createElement('div');
      const heading = document.createElement('h5');
      const meta = document.createElement('p');
      const eventDate = this.formatDate(event.occurred_at, true);

      marker.className = 'pp-order-tracking__timeline-marker';
      marker.setAttribute('aria-hidden', 'true');
      heading.textContent = this.statusLabel(event.status);
      meta.textContent = [eventDate, event.location].filter(Boolean).join(' · ');
      content.append(heading);
      if (meta.textContent) content.append(meta);
      item.append(marker, content);
      this.timeline.append(item);
    });

    this.timelineWrap.hidden = events.length === 0;
    this.result.hidden = false;
    this.result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  handleSubmit = async (event) => {
    event.preventDefault();
    if (!this.form?.reportValidity()) return;

    const trackingNumber = this.input.value.trim();
    if (!trackingNumber) {
      return;
    }

    this.submitButton.disabled = true;
    this.form.setAttribute('aria-busy', 'true');
    this.result.hidden = true;
    this.status.textContent = this.dataset.loading;

    try {
      const response = await fetch(this.dataset.endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: new URLSearchParams({ tracking_number: trackingNumber }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok || !payload?.shipment) {
        const errorCode = payload?.error?.code || payload?.error;
        const errorMessage = response.status === 400 || errorCode === 'invalid_tracking_number'
          ? this.dataset.invalid
          : errorCode === 'not_found'
            ? this.dataset.notFound
            : this.dataset.unavailable;
        throw new Error(errorMessage);
      }

      this.status.textContent = '';
      this.renderResult(payload.shipment, trackingNumber);
    } catch (error) {
      this.status.textContent = error.message || this.dataset.unavailable;
    } finally {
      this.submitButton.disabled = false;
      this.form.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('pp-order-tracking')) {
  customElements.define('pp-order-tracking', PPOrderTracking);
}
